// ============================================================================
// BehaviorLocationBreakdown.tsx
// Section component for displaying behavior incidents by location
// ============================================================================

import { Bar } from "react-chartjs-2";
import { Fragment, useEffect, useState } from "react";
import { Container, Child, Header, LayerContent } from "../styles";
import { ChartContainer, NoData } from "../../style";
import { useAnalyticsWrapper } from "../../../../../store/store";
import {
  barOptions,
  handlePointClicked,
} from "../formUtils";
import SectionModal from "./BehaviorAnalysisSectionModal";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { behaviorAnalysisDetailCols } from "../../dummyData/colums";
import { Grid, GridColumn, GridColumnProps } from "@progress/kendo-react-grid";
import {
  CenteredWholeNumCell,
  HeaderCenteredCell,
} from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";
import useFormDataHelpers from "../../../../../hooks/useFormDataHelpers";
import { MITIGATE_COLORS } from "../../../../../shared/sharedConstants";

// ============================================================================
// Props Interface
// ============================================================================
interface BehaviorLocationBreakdownProps {
  setClickedIncident: (item: any) => void;
  isFetchingSelectedIncident: boolean;
  shareTableData?: (data: any[]) => void;
}

// ============================================================================
// Row Type Definition - location, count, and percentage
// ============================================================================
type LocationRow = {
  location: string;
  count: number;
  percentage: number;
};

// ============================================================================
// API Response Types
// ============================================================================
interface LocationDataItem {
  location: string;
  incidentCount: number;
  percentage?: number;
}

type LocationDrilldown = Record<string, any[]>;

// ============================================================================
// Component
// ============================================================================
const BehaviorLocationBreakdown = ({
  setClickedIncident,
  isFetchingSelectedIncident,
  shareTableData,
}: BehaviorLocationBreakdownProps) => {
  // ---------------------------------------------------------------------------
  // Helper Hooks
  // ---------------------------------------------------------------------------
  const { getFacilityLabel } = useFormDataHelpers();

  // ---------------------------------------------------------------------------
  // Local State
  // ---------------------------------------------------------------------------
  const [tableData, setTableData] = useState<LocationRow[]>([]);
  const [grid, setGrid] = useState<any>([]);
  const [barData, setBarData] = useState<any>([]);
  const [modalData, setModalData] = useState<any>([]);
  const [location, setLocation] = useState("");
  const [showLocationModal, setShowLocationModal] = useState(false);

  // ---------------------------------------------------------------------------
  // Global State from Zustand
  // ---------------------------------------------------------------------------
  const { startDate, endDate, behaviorFacility, behaviorAnalysisIncidents } =
    useAnalyticsWrapper((state) => ({
      startDate: state.startDate,
      endDate: state.endDate,
      behaviorFacility: state.behaviorFacility,
      behaviorAnalysisIncidents: state.behaviorAnalysisIncidents,
    }));

  // Derived value for facility name
  const facilityLabel = getFacilityLabel(behaviorFacility);

  // ---------------------------------------------------------------------------
  // Data Transformation Function
  // ---------------------------------------------------------------------------
  const adjustedTableData = (locationData: LocationDataItem[]): LocationRow[] => {
    const returnData: LocationRow[] = [];

    if (!locationData?.length) {
      returnData.push({ location: "Locations Total", count: 0, percentage: 0 });
      return returnData;
    }

    // Calculate total incidents first for percentage calculation
    const totalIncidents = locationData.reduce((sum, item) => sum + (item.incidentCount || 0), 0);

    // Transform each location from API data
    locationData.forEach((item) => {
      const count = item.incidentCount || 0;
      const percentage = totalIncidents > 0 
        ? Math.round((count / totalIncidents) * 10000) / 100 
        : 0;
      returnData.push({
        location: item.location,
        count: count,
        percentage: percentage,
      });
    });

    // Add totals row
    returnData.push({
      location: "Locations Total",
      count: totalIncidents,
      percentage: 100,
    });

    return returnData;
  };

  // ---------------------------------------------------------------------------
  // Transform table data for bar chart (ensures chart order matches table order)
  // ---------------------------------------------------------------------------
  const transformTableDataForChart = (rows: LocationRow[]) => {
    // Filter out the totals row for the chart
    const dataRows = rows.filter(row => row.location !== "Locations Total");
    
    if (dataRows.length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = dataRows.map(row => row.location);
    const data = dataRows.map(row => row.count);

    // Alternating colors from MITIGATE_COLORS
    const alternatingColors = [
      MITIGATE_COLORS.BLUE[0],
      MITIGATE_COLORS.GREEN[0],
      MITIGATE_COLORS.RED[0],
      MITIGATE_COLORS.ORANGE[0],
      MITIGATE_COLORS.PURPLE[0],
      MITIGATE_COLORS.BLUE[1],
      MITIGATE_COLORS.GREEN[1],
      MITIGATE_COLORS.RED[1],
      MITIGATE_COLORS.ORANGE[1],
      MITIGATE_COLORS.PURPLE[1],
      MITIGATE_COLORS.BLUE[2],
      MITIGATE_COLORS.GREEN[2],
      MITIGATE_COLORS.RED[2],
      MITIGATE_COLORS.ORANGE[2],
      MITIGATE_COLORS.PURPLE[2],
    ];

    return {
      labels,
      datasets: [
        {
          label: "# of Incidents",
          data,
          backgroundColor: alternatingColors.slice(0, labels.length),
          borderWidth: 0,
        },
      ],
    };
  };

  // ---------------------------------------------------------------------------
  // Process data when behaviorAnalysisIncidents changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (behaviorAnalysisIncidents !== undefined) {
      const locationSection = behaviorAnalysisIncidents?.locationSection;
      const breakdownData = locationSection?.locationBreakdown;
      
      const transformedData = adjustedTableData(breakdownData);
      const transformedBarData = transformTableDataForChart(transformedData);

      setTableData(transformedData);
      setBarData(transformedBarData);
      shareTableData?.(transformedData);
    }
  }, [behaviorAnalysisIncidents]);

  const title = "Behavior Location Breakdown";

  // ---------------------------------------------------------------------------
  // Handle clicking a bar in the chart
  // ---------------------------------------------------------------------------
  const handleBarClicked = (chart: any) => {
    if (!chart.length) return;
    const label = handlePointClicked(chart, barData?.labels);
    setShowLocationModal(true);
    setLocation(label);
    // Get drilldown data for the clicked location from locationSection
    const drilldownData = behaviorAnalysisIncidents?.locationSection?.locationDrilldown || {};
    const filteredDetails = drilldownData[label] || [];
    setModalData(filteredDetails);
  };

  // ---------------------------------------------------------------------------
  // Build Kendo Grid when tableData changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const cellCountProps = {
      cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell },
    };
    const cellPercentageProps = {
      cells: { data: CenteredWholeNumCell, append: '%', headerCell: HeaderCenteredCell },
    };

    const grid = (
      <Fragment>
        <Grid data={tableData || []} scrollable="none" rows={{ data: TotalRow }}>
          <GridColumn field="location" title="Location" />
          <GridColumn field="count" title="# of Incidents" {...(cellCountProps as GridColumnProps)} />
          <GridColumn field="percentage" title="%" {...(cellPercentageProps as GridColumnProps)} />
        </Grid>
      </Fragment>
    );
    setGrid(grid);
  }, [tableData]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <LayerContent>
      <Header>{title}</Header>
      <Container>
        <Child flex="40%">
          {grid}
        </Child>
        <Child flex="60%">
          <ChartContainer>
            {barData?.labels?.length > 0 ? (
              <Bar options={barOptions(handleBarClicked)} data={barData} />
            ) : (
              <NoData>No Data</NoData>
            )}
          </ChartContainer>
        </Child>
      </Container>

      {showLocationModal && (
        <SectionModal
          open={showLocationModal}
          facilityName={facilityLabel}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowLocationModal(false)}
          resetFn={() => {}}
          isLoading={false}
          tableData={modalData}
          tableCols={behaviorAnalysisDetailCols()}
          title={`${title} - ${location}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: modalData,
              columnData: behaviorAnalysisDetailCols(),
              fileName: facilityLabel,
              startDate,
              endDate,
              subTitle: `${title} - ${location}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              modalData,
              behaviorAnalysisDetailCols(),
              `${facilityLabel} - ${title} - ${location}`
            )
          }
        />
      )}
    </LayerContent>
  );
};

export default BehaviorLocationBreakdown;
